// src/components/PlatformHomeAspirante.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  useMediaQuery,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import ProfilePlatform from "./ProfilePlatform";
import ProfilePlatformSmall from "./ProfilePlatformSmall";
import TusProgramas from "./TusProgramas";
import { getAuth } from "firebase/auth";

const API_BASE = "https://vol-backend.onrender.com";


const DOCUMENTS_LINK = "/MiPerfil";

// Colores por tipo de aviso
const TIPO_META = {
  urgente:     { label: "Urgente",     bg: "#ffebee", border: "#c62828", color: "#c62828" },
  informativo: { label: "Informativo", bg: "#e3f2fd", border: "#1565c0", color: "#1565c0" },
  evento:      { label: "Evento",      bg: "#f3e5f5", border: "#6a1b9a", color: "#6a1b9a" },
  anuncio:     { label: "Anuncio",     bg: "#e8f5e9", border: "#2e7d32", color: "#2e7d32" },
};

export default function PlatformHomeAspirante({ userInfo = null }) {
  const isSmallScreen = useMediaQuery("(max-width:1232px)");

  // ── Avisos globales ───────────────────────────────────────
  const [avisos, setAvisos]               = useState([]);
  const [loadingAvisos, setLoadingAvisos] = useState(true);
  const [expandAvisos, setExpandAvisos]   = useState(false);

  useEffect(() => {
    const fetchAvisos = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        const res = await fetch(`${API_BASE}/avisos/publicos?principal=1&limit=10`, {
          headers: { Authorization: `Bearer ${token}`, "x-firebase-uid": user.uid },
        });
        if (!res.ok) return;
        const data = await res.json();
        setAvisos(Array.isArray(data?.avisos) ? data.avisos : Array.isArray(data) ? data : []);
      } catch (e) {
        console.warn("⚠️ No se pudieron cargar avisos:", e.message);
      } finally {
        setLoadingAvisos(false);
      }
    };
    fetchAvisos();
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isSmallScreen ? "column" : "row",
        justifyContent: isSmallScreen ? "center" : "space-between",
        alignItems: "flex-start",
        gap: 3,
      }}
    >
      {/* 🔸 COLUMNA CENTRAL – botón + avisos + dummy */}
      <Box
        sx={{
          flex: 1,
          minHeight: 200,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* BOTÓN DOCUMENTOS (centrado) */}
        <Button
          component="a"
          href={DOCUMENTS_LINK}
          sx={{
            alignSelf: "center",       // ⬅️ centrado
            width: "100%",
            maxWidth: 600,             // no más ancho de esto
            backgroundColor: "#ff3333",
            color: "#fff",
            textTransform: "none",
            fontFamily: "'Arial', sans-serif",
            borderRadius: "999px",
            px: 3,
            py: 1.2,
            fontSize: "0.9rem",
            boxShadow: "0 4px 12px rgba(255, 51, 51, 0.25)",
            textAlign: "center",
            "&:hover": {
              backgroundColor: "#e02a2a",
              textDecoration: "none",
              boxShadow: "0 6px 16px rgba(255, 51, 51, 0.35)",
            },
          }}
        >
          Para continuar con tu registro, no olvides subir tus documentos. Da clic aquí.
        </Button>

        {/* CARD AVISOS — dinámico */}
        <Box sx={{ backgroundColor: "#ffffff", borderRadius: 3, boxShadow: "0 5px 14px rgba(255, 0, 0, 0.1)" }}>
          {/* HEADER */}
          <Box sx={{ backgroundColor: "#e6dfef", textAlign: "center", py: 1.3, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
            <Typography sx={{ fontFamily: "'Arial', sans-serif", fontWeight: 700, fontSize: "0.9rem", textTransform: "uppercase" }}>
              AVISOS
            </Typography>
          </Box>

          {/* CONTENIDO */}
          <Box sx={{ px: 2.2, py: 1.5 }}>
            {loadingAvisos ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={20} sx={{ color: "#c8102e" }} />
              </Box>

            ) : avisos.length === 0 ? (
              // ── Fallback bienvenida ──────────────────────────────
              <Box sx={{ backgroundColor: "#fff8ff", borderRadius: 2, p: 1.8 }}>
                <Typography sx={{ fontFamily: "'Arial', sans-serif", fontSize: "0.82rem", color: "#867d91", lineHeight: 1.5, textAlign: "center" }}>
                  <strong>¡Bienvenido/a!</strong>
                  <br /> Por favor, completa tu perfil y sube tus documentos.
                  ¡Ya puedes comenzar con tu formación!.<br />
                  Recuerda que primero verás temas institucionales, posteriormente
                  podrás elegir el programa al cual quieres apoyar, podrás formarte
                  respecto a ello y finalmente, te integrarás a las operaciones. ¡Bienvenido/a!
                </Typography>
              </Box>

            ) : (
              // ── Aviso(s) principales ─────────────────────────────
              <Box>
                {/* Primer aviso — siempre visible */}
                {(() => {
                  const aviso = avisos[0];
                  const meta = TIPO_META[aviso.tipo?.toLowerCase()] || TIPO_META.informativo;
                  return (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 0.5 }}>
                      <Chip label={meta.label} size="small"
                        sx={{ fontSize: 10, height: 20, fontWeight: 700,
                          backgroundColor: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }} />
                      <Typography sx={{ fontFamily: "'Arial', sans-serif", fontWeight: 700, fontSize: "0.85rem", color: "#2d233a", lineHeight: 1.3 }}>
                        {aviso.titulo}
                      </Typography>
                      <Typography sx={{ fontFamily: "'Arial', sans-serif", fontSize: "0.78rem", color: "#867d91", lineHeight: 1.4 }}>
                        {aviso.mensaje?.length > 140 ? aviso.mensaje.slice(0, 140) + "…" : aviso.mensaje}
                      </Typography>
                      {aviso.boton_label && aviso.boton_url && (
                        <Button href={aviso.boton_url} target="_blank" rel="noopener noreferrer" size="small"
                          sx={{ fontSize: "0.72rem", textTransform: "none", color: meta.color, p: 0, fontWeight: 700 }}>
                          {aviso.boton_label} →
                        </Button>
                      )}
                    </Box>
                  );
                })()}

                {/* Avisos adicionales — colapsables */}
                {avisos.length > 1 && (
                  <>
                    {expandAvisos && (
                      <Box sx={{ mt: 1.2 }}>
                        {avisos.slice(1).map((aviso, idx) => {
                          const meta = TIPO_META[aviso.tipo?.toLowerCase()] || TIPO_META.informativo;
                          return (
                            <Box key={aviso.id || idx}>
                              <Divider sx={{ borderColor: "#f0eaf5", my: 1 }} />
                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 0.5 }}>
                                <Chip label={meta.label} size="small"
                                  sx={{ fontSize: 10, height: 20, fontWeight: 700,
                                    backgroundColor: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }} />
                                <Typography sx={{ fontFamily: "'Arial', sans-serif", fontWeight: 700, fontSize: "0.83rem", color: "#2d233a", lineHeight: 1.3 }}>
                                  {aviso.titulo}
                                </Typography>
                                <Typography sx={{ fontFamily: "'Arial', sans-serif", fontSize: "0.77rem", color: "#867d91", lineHeight: 1.4 }}>
                                  {aviso.mensaje?.length > 120 ? aviso.mensaje.slice(0, 120) + "…" : aviso.mensaje}
                                </Typography>
                                {aviso.boton_label && aviso.boton_url && (
                                  <Button href={aviso.boton_url} target="_blank" rel="noopener noreferrer" size="small"
                                    sx={{ fontSize: "0.72rem", textTransform: "none", color: meta.color, p: 0, fontWeight: 700 }}>
                                    {aviso.boton_label} →
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                    <Divider sx={{ borderColor: "#f0eaf5", mt: 1, mb: 0.5 }} />
                    <Button fullWidth size="small"
                      onClick={() => setExpandAvisos(v => !v)}
                      sx={{ fontSize: "0.75rem", textTransform: "none", color: "#c8102e", fontWeight: 700,
                        "&:hover": { backgroundColor: "#fff0f0" } }}>
                      {expandAvisos
                        ? "Ver menos ▲"
                        : `Ver ${avisos.length - 1} aviso${avisos.length - 1 > 1 ? "s" : ""} más ▼`}
                    </Button>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* CARD DUMMY PARA FUTURO COMPONENTE */}
        <TusProgramas userInfo={userInfo}/>
      </Box>

      {/* 🔹 PERFIL – SIEMPRE A LA DERECHA EN ESCRITORIO */}
      <Box
        sx={{
          width: isSmallScreen ? "100%" : "28%",
          flexShrink: 0,
        }}
      >
        {isSmallScreen ? <ProfilePlatformSmall /> : <ProfilePlatform />}
      </Box>
    </Box>
  );
}